'use client'

import { useIsTabletOrMobileScreen } from '@components/hooks/useIsMobileScreen'
import type { Url } from '@lib/routes'
import ApiRoutes from '@lib/routes/ApiRoutes'
import absoluteUrl from '@lib/routes/helpers/absoluteUrl'
import urlWithQueryParams from '@lib/routes/helpers/urlWithQueryParams'
import { slugify } from '@lib/utilities/text/slugify'
import updateFacetFilterFromSlug from '@modules/trials/search/facetFilters/updateFacetFilterFromSlug'
import debounce from 'lodash/debounce'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useState } from 'react'

export type LocationFacetType = 'city' | 'state'

export interface SelectedLocation {
  facet: LocationFacetType
  value: string
}

export interface UseLocationFacetRefinementResult {
  handlePredictionClick: (
    prediction: google.maps.places.AutocompletePrediction,
  ) => void
  isMobile?: boolean
  navigateWithFilters: (url: Url | undefined) => void
  predictions: google.maps.places.AutocompletePrediction[]
  searchTerm: string
  selectedLocation?: SelectedLocation
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>
  setSelectedLocation: React.Dispatch<
    React.SetStateAction<SelectedLocation | undefined>
  >
}

// Helper function that to trim gogle place prediction descriptions
function slugifyLocationPrediction(description: string): string {
  const trimmed = description.replace(/, USA|, Canada/gi, '')
  return slugify(trimmed)
}

export function useLocationFacetRefinement({
  initialLocation,
}: {
  initialLocation?: string
}): UseLocationFacetRefinementResult {
  const [searchTerm, setSearchTerm] = useState<string>(initialLocation ?? '')
  const [predictions, setPredictions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([])
  const [selectedLocation, setSelectedLocation] = useState<
    SelectedLocation | undefined
  >(undefined)
  const isMobile = useIsTabletOrMobileScreen()

  const pathname = usePathname()
  const router = useRouter()
  const searchParams = new URLSearchParams(useSearchParams() ?? '')

  if (searchParams.has('page')) {
    searchParams.delete('page')
  }

  const debouncedGetPredictions = useCallback(
    debounce(async (query: string) => {
      if (query.trim() && query.trim().length > 2) {
        try {
          const response = await fetch(
            urlWithQueryParams(ApiRoutes.v1.location.autocomplete, {
              query: query,
            }),
            {
              cache: 'force-cache',
              headers: {
                'Content-Type': 'application/json',
              },
              method: 'GET',
              next: { revalidate: 60 * 60 * 24 },
            },
          )

          if (!response.ok) {
            throw new Error('Failed to fetch predictions')
          }

          const data = await response.json()
          setPredictions(data.predictions || [])
        } catch (error) {}
      } else {
        setPredictions([])
      }
    }, 300),
    [],
  )

  // Navigation helper used by both selection and when clearing the search
  const navigateWithFilters = useCallback(
    (url: Url | undefined) => {
      if (url) {
        router.push(
          absoluteUrl(
            urlWithQueryParams(url.toString(), {
              ...(isMobile ? { showFilters: 'true' } : {}),
              ...Object.fromEntries(searchParams),
            }),
          ),
        )
      }
    },
    [router, searchParams],
  )

  // Slugify the google place prediction and update the relevant facet filter
  function handlePredictionClick(
    prediction: google.maps.places.AutocompletePrediction,
  ) {
    const locationValue = slugifyLocationPrediction(prediction.description)
    const facet: LocationFacetType = prediction.types.includes(
      'administrative_area_level_1',
    )
      ? 'state'
      : 'city'
    setSelectedLocation({ facet, value: locationValue })
    const url = updateFacetFilterFromSlug({
      facet: facet,
      slug: pathname,
      value: locationValue,
    })
    navigateWithFilters(url)
  }

  useEffect(() => {
    if (searchTerm) {
      debouncedGetPredictions(searchTerm)
    } else {
      setPredictions([])
    }
  }, [searchTerm])

  return {
    handlePredictionClick,
    navigateWithFilters,
    predictions,
    searchTerm,
    selectedLocation,
    setSearchTerm,
    setSelectedLocation,
  }
}
